import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu } from 'lucide-react';
import { usePreferencesStore } from '../../store/preferencesStore';
import { cn } from '../../utils/cn';
import { AiToolsSheet } from './AiToolsSheet';
import { SvgIcon, ICON_HOME, ICON_AI, ICON_FILES, ICON_HISTORY } from './navConfig';

interface Tab {
  key: string;
  label: string;
  icon: JSX.Element;
  isActive: (pathname: string) => boolean;
  to?: string;        // link tab
  onClick?: () => void; // action tab (e.g. opens the AI tools sheet)
}

const tabClass =
  'flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[44px] px-1 py-1 text-[10px] font-medium transition-colors';

export function MobileBottomNav() {
  const { pathname } = useLocation();
  const openMobileNav = usePreferencesStore((s) => s.openMobileNav);

  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = () => setSheetOpen(false);

  const homeTab: Tab = {
    key: 'home', label: 'Home', to: '/dashboard',
    icon: <SvgIcon path={ICON_HOME} />,
    isActive: (p) => p === '/dashboard',
  };
  const historyTab: Tab = {
    key: 'history', label: 'History', to: '/ai-tools/history',
    icon: <SvgIcon path={ICON_HISTORY} />,
    isActive: (p) => p.startsWith('/ai-tools/history'),
  };

  // Assemble up to four primary tabs; the fifth slot is always "More".
  const tabs: Tab[] = [
    homeTab,
    {
      key: 'ai', label: 'AI Tools',
      icon: <SvgIcon path={ICON_AI} />,
      onClick: () => setSheetOpen((v) => !v),
      isActive: (p) =>
        sheetOpen ||
        (p.startsWith('/ai-tools') && !p.startsWith('/ai-tools/history')) ||
        p.startsWith('/journals'),
    },
    {
      key: 'files', label: 'Files', to: '/files',
      icon: <SvgIcon path={ICON_FILES} />,
      isActive: (p) => p.startsWith('/files'),
    },
    historyTab,
  ];

  return (
    <>
      {/* Transparent outside-tap catcher — closes the sheet without dimming.
          Sits below the bottom bar (z-40) so the nav stays fully interactive. */}
      <AnimatePresence>
        {sheetOpen && (
          <motion.div
            className="fixed inset-0 z-30 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeSheet}
          />
        )}
      </AnimatePresence>

      {/* Bottom-anchored container: sheet stacks directly above the nav. */}
      <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden">
        <AiToolsSheet open={sheetOpen} onClose={closeSheet} />

        <nav
          className="flex h-16 items-stretch border-t border-gray-200 bg-white/95 backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-950/95"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Primary"
        >
          {tabs.map((tab) => {
            const active = tab.isActive(pathname);
            const inner = (
              <>
                <span className={cn('flex h-6 w-6 items-center justify-center', active && 'scale-105')}>
                  {tab.icon}
                </span>
                <span className="truncate max-w-full">{tab.label}</span>
              </>
            );
            const cls = cn(
              tabClass,
              active
                ? 'text-brand-600 dark:text-brand-400'
                : 'text-gray-500 hover:text-gray-800 dark:text-zinc-500 dark:hover:text-zinc-200',
            );
            return tab.to ? (
              <Link key={tab.key} to={tab.to} className={cls} onClick={closeSheet}>
                {inner}
              </Link>
            ) : (
              <button key={tab.key} type="button" onClick={tab.onClick} className={cls} aria-expanded={tab.key === 'ai' ? sheetOpen : undefined}>
                {inner}
              </button>
            );
          })}

          {/* More — opens the full navigation drawer */}
          <button
            onClick={() => { closeSheet(); openMobileNav(); }}
            className={cn(tabClass, 'text-gray-500 hover:text-gray-800 dark:text-zinc-500 dark:hover:text-zinc-200')}
            aria-label="More navigation"
          >
            <span className="flex h-6 w-6 items-center justify-center">
              <Menu className="h-5 w-5" />
            </span>
            <span>More</span>
          </button>
        </nav>
      </div>
    </>
  );
}
